import type { FormEvent } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { AuthShell } from './AuthShell';
import { authApi } from './api';

type ActivationStep = 'VERIFYING' | 'PASSWORD' | 'MFA' | 'RECOVERY' | 'ERROR';

interface AuthenticatorSetup {
  readonly authenticatorKey: string;
  readonly provisioningUri: string;
}

function activationToken(): string {
  return new URLSearchParams(window.location.search).get('token') ?? '';
}

export function ActivationView({
  onReturnToLogin,
}: {
  readonly onReturnToLogin: () => void;
}): React.JSX.Element {
  const token = useMemo(activationToken, []);
  const [step, setStep] = useState<ActivationStep>('VERIFYING');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [setup, setSetup] = useState<AuthenticatorSetup | null>(null);
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<readonly string[]>([]);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      if (token.length === 0) {
        setStep('ERROR');
        return;
      }

      try {
        await authApi.verifyActivation(token);
        if (!cancelled) setStep('PASSWORD');
      } catch {
        if (!cancelled) setStep('ERROR');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  async function prepareAuthenticator(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    if (password !== confirmation) {
      setError('Les deux mots de passe doivent être identiques.');
      return;
    }

    setPending(true);
    try {
      const result = await authApi.prepareActivation(token, password);
      setSetup({
        authenticatorKey: result.authenticatorKey,
        provisioningUri: result.provisioningUri,
      });
      setPassword('');
      setConfirmation('');
      setStep('MFA');
    } catch {
      setError(
        'Le lien est invalide, expiré ou le mot de passe ne respecte pas la politique de sécurité.',
      );
    } finally {
      setPending(false);
    }
  }

  async function completeActivation(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setPending(true);
    try {
      const result = await authApi.completeActivation(token, code);
      setRecoveryCodes(result.recoveryCodes);
      setSetup(null);
      setCode('');
      window.history.replaceState({}, '', '/activation');
      setStep('RECOVERY');
    } catch {
      setError('Le code est invalide, a expiré ou l’activation ne peut plus être terminée.');
    } finally {
      setPending(false);
    }
  }

  if (step === 'VERIFYING') {
    return (
      <AuthShell
        eyebrow="Activation du compte"
        title="Vérification de l’invitation"
        intro="Tosho vérifie votre lien d’activation avant de poursuivre la sécurisation du compte."
      />
    );
  }

  if (step === 'ERROR') {
    return (
      <AuthShell
        eyebrow="Activation du compte"
        title="Invitation indisponible"
        intro="Ce lien d’activation est invalide, expiré, déjà utilisé ou n’est plus disponible."
      >
        <div className="auth-form">
          <button
            className="auth-button auth-button--primary"
            type="button"
            onClick={onReturnToLogin}
          >
            Retour à la connexion
          </button>
        </div>
      </AuthShell>
    );
  }

  if (step === 'PASSWORD') {
    return (
      <AuthShell
        eyebrow="Activation du compte"
        title="Créez votre mot de passe"
        intro="Choisissez un mot de passe d’au moins 15 caractères. Tosho vérifiera également qu’il n’est pas compromis."
      >
        <form className="auth-form" onSubmit={(event) => void prepareAuthenticator(event)}>
          <label className="auth-field">
            <span>Nouveau mot de passe</span>
            <input
              type="password"
              autoComplete="new-password"
              minLength={15}
              required
              value={password}
              onChange={(event) => setPassword(event.currentTarget.value)}
            />
          </label>
          <label className="auth-field">
            <span>Confirmer le mot de passe</span>
            <input
              type="password"
              autoComplete="new-password"
              minLength={15}
              required
              value={confirmation}
              onChange={(event) => setConfirmation(event.currentTarget.value)}
            />
          </label>
          {error ? (
            <p className="auth-message tosho-feedback is-error" role="alert">
              {error}
            </p>
          ) : null}
          <button className="auth-button auth-button--primary" type="submit" disabled={pending}>
            {pending ? 'Vérification…' : 'Continuer'}
          </button>
        </form>
      </AuthShell>
    );
  }

  if (step === 'MFA' && setup !== null) {
    return (
      <AuthShell
        eyebrow="Activation du compte"
        title="Configurez Microsoft Authenticator"
        intro="Ajoutez Tosho dans Microsoft Authenticator, puis saisissez le code à 6 chiffres généré par l’application."
      >
        <form className="auth-form" onSubmit={(event) => void completeActivation(event)}>
          <div className="auth-activation-key">
            <span>Clé de configuration manuelle</span>
            <code>{setup.authenticatorKey}</code>
          </div>
          <a className="auth-button auth-button--secondary" href={setup.provisioningUri}>
            Ouvrir dans Microsoft Authenticator
          </a>
          <label className="auth-field">
            <span>Code d’authentification</span>
            <input
              className="auth-field__code"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              maxLength={6}
              required
              value={code}
              onChange={(event) =>
                setCode(event.currentTarget.value.replace(/\D/g, '').slice(0, 6))
              }
            />
          </label>
          {error ? (
            <p className="auth-message tosho-feedback is-error" role="alert">
              {error}
            </p>
          ) : null}
          <button
            className="auth-button auth-button--primary"
            type="submit"
            disabled={pending || code.length !== 6}
          >
            {pending ? 'Activation…' : 'Activer mon compte'}
          </button>
        </form>
        <aside className="auth-security-note">
          <span className="auth-security-note__icon" aria-hidden="true">
            ◇
          </span>
          <span>
            <strong>MFA obligatoire</strong>
            <small>Le secret d’enrôlement reste affiché uniquement pendant cette étape.</small>
          </span>
        </aside>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      eyebrow="Activation du compte"
      title="Conservez vos codes de récupération"
      intro="Votre compte est activé. Conservez ces 10 codes dans un endroit sûr : Tosho ne les affichera plus."
    >
      <div className="auth-form">
        <div className="auth-recovery-codes" aria-label="Codes de récupération">
          {recoveryCodes.map((recoveryCode) => (
            <code key={recoveryCode}>{recoveryCode}</code>
          ))}
        </div>
        <button
          className="auth-button auth-button--primary"
          type="button"
          onClick={onReturnToLogin}
        >
          Retour à la connexion
        </button>
      </div>
      <aside className="auth-security-note">
        <span className="auth-security-note__icon" aria-hidden="true">
          ◇
        </span>
        <span>
          <strong>Affichage unique</strong>
          <small>
            Chaque code de récupération est à usage unique et n’est conservé par Tosho que sous
            forme d’empreinte.
          </small>
        </span>
      </aside>
    </AuthShell>
  );
}
